import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, TextInput, Textarea, Switch } from '@mantine/core';
import { useEmailTemplate, useUpdateEmailTemplate } from '@/hooks/api-hooks';
import PageHeader from '@/components/page-header';
import { LexicalEditor } from '@/components/lexical-editor';
import { SkeletonList } from '@/components/snippets';

interface EmailTemplate {
    id?: number;
    name?: string;
    subject?: string;
    html_content?: string;
    [key: string]: any;
}

export default function EditEmailTemplatePage() {
    const { t } = useTranslation();
    const { id } = useParams<{ id: string }>();

    const [draft, setDraft] = useState<EmailTemplate>({});
    const [backup, setBackup] = useState<EmailTemplate>({});
    const [saving, setSaving] = useState<string>('NONE');
    const [isRawMode, setIsRawMode] = useState(false);

    const {
        data: templateResponse,
        isLoading
    } = useEmailTemplate(id as string);
    const updateTemplateMutation = useUpdateEmailTemplate();

    const template = templateResponse?.data;

    useEffect(() => {
        if (template) {
            setDraft(template);
            setBackup(template);
        }
    }, [template]);

    const handleInputChange = (field: keyof EmailTemplate, value: any) => {
        setDraft(prev => ({ ...prev, [field]: value }));
    };

    const saveSection = (sectionFields: Record<string, any>, section: string = 'section') => {
        setSaving(section);
        const newData = { ...template, ...sectionFields };
        updateTemplateMutation.mutate(
            { id: Number(id), ...newData },
            {
                onSuccess: () => {
                    setSaving('NONE');
                    setBackup({ ...draft });
                },
                onError: () => {
                    setSaving('NONE');
                }
            }
        );
    };

    const hasUnsavedChanges = (fields: string[]) => {
        return fields.some(field => draft[field as keyof EmailTemplate] !== backup[field as keyof EmailTemplate]);
    };

    if (isLoading || !template) {
        return (
            <div className="panel-content">
                <PageHeader title={t('edit_template')} />
                <SkeletonList />
            </div>
        );
    }

    return (
        <div className="panel-content">
            <div className="flex-1 flex flex-col gap-4">
                <div className='solid-box-border space-y-2'>
                    <TextInput
                        label={t('template_name')}
                        className='font-bold'
                        value={draft.name || ''}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                    />
                    <TextInput
                        label={t('email_subject')}
                        value={draft.subject || ''}
                        onChange={(e) => handleInputChange('subject', e.target.value)}
                    />
                    <Button
                        size="sm"
                        color='black'
                        disabled={!hasUnsavedChanges(['name', 'subject']) || saving === 'basic_info'}
                        loading={saving === 'basic_info'}
                        onClick={() => saveSection({ name: draft.name, subject: draft.subject }, 'basic_info')}
                    >
                        {t('save')}
                    </Button>
                </div>

                <div className='space-y-2'>
                    <div className="flex items-center justify-between mb-2">
                        <div className='font-bold text-sm'>{t('email_content')}</div>
                        <Switch
                            label={t('raw_html_mode', 'Raw HTML Mode')}
                            checked={isRawMode}
                            onChange={(event) => setIsRawMode(event.currentTarget.checked)}
                            size="sm"
                        />
                    </div>
                    <div className='flex gap-1'>
                        {isRawMode ? (
                            <Textarea
                                className="flex-1"
                                autosize
                                minRows={15}
                                value={draft.html_content || ''}
                                onChange={(e) => handleInputChange('html_content', e.target.value)}
                                placeholder="<html><body>...</body></html>"
                            />
                        ) : (
                            <LexicalEditor
                                initialHtml={draft.html_content || ''}
                                onChange={(html) => handleInputChange('html_content', html)}
                                placeholder={t('email_content_placeholder')}
                            />
                        )}
                        <div className="solid-box-border-tinted w-50 text-xs h-fit">
                            <div className='font-bold text-sm mb-2'>{t('available_variables')}:</div>
                            <ul className="list-disc pl-4 space-y-1 text-gray-700">
                                <li className=" bg-white px-1 py-0.5 rounded border border-gray-200">{'{{client_name}}'}</li>
                                <li className=" bg-white px-1 py-0.5 rounded border border-gray-200">{'{{tenant_name}}'}</li>
                                <li className=" bg-white px-1 py-0.5 rounded border border-gray-200">{'{{tenant_logo_url}}'}</li>
                                <li className=" bg-white px-1 py-0.5 rounded border border-gray-200">{'{{client_tariff_url}}'}</li>
                                <li className=" bg-white px-1 py-0.5 rounded border border-gray-200">{'{{tenant_address}}'}</li>
                                <li className=" bg-white px-1 py-0.5 rounded border border-gray-200">{'{{tenant_phone}}'}</li>
                                <li className=" bg-white px-1 py-0.5 rounded border border-gray-200">{'{{tenant_email}}'}</li>
                                <li className=" bg-white px-1 py-0.5 rounded border border-gray-200">{'{{client_id}}'}</li>
                                <li className=" bg-white px-1 py-0.5 rounded border border-gray-200">{'{{magicLinkUrl}}'}</li>
                            </ul>
                        </div>
                    </div>
                    <Button
                        size="sm"
                        color='black'
                        disabled={!hasUnsavedChanges(['html_content']) || saving === 'content'}
                        loading={saving === 'content'}
                        onClick={() => saveSection({ html_content: draft.html_content }, 'content')}
                    >
                        {t('save')}
                    </Button>
                </div>

            </div>
        </div>
    );
}
